import React, { useEffect, useMemo, useState } from "react";
import { Modal, Table, Alert, Flex, Typography, Button } from "antd";
import {
  TARGET_MEMBER_FOUND,
  TARGET_MEMBER_NOT_FOUND,
} from "../../utils/targetMember";

const { Title } = Typography;

const COLUMN_LABELS: Record<string, string> = {
  "Email Address": "Email",
};

const STATUS_DOT_COLOR: Record<string, string> = {
  [TARGET_MEMBER_FOUND]: "#12B76A",
  [TARGET_MEMBER_NOT_FOUND]: "#F04438",
};

interface TargetMemberPreviewModalProps {
  open: boolean;
  onClose: () => void;
  /** Raw rows parsed from the uploaded Excel file (header-keyed), optionally
   * annotated with a "Status" field (Found/Not Found) once checked. */
  rows: Record<string, any>[];
}

const TargetMemberPreviewModal: React.FC<TargetMemberPreviewModalProps> = ({
  open,
  onClose,
  rows,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const isMobile = window.innerWidth < 768;

  useEffect(() => {
    if (open) {
      setCurrentPage(1);
      setPageSize(10);
    }
  }, [open]);

  const hasStatus = rows.length > 0 && "Status" in rows[0];
  const foundCount = useMemo(
    () => rows.filter((r) => r.Status === TARGET_MEMBER_FOUND).length,
    [rows],
  );
  const notFoundCount = rows.length - foundCount;

  const summaryMessage = !hasStatus
    ? "Preview of the uploaded target member list."
    : foundCount === 0
      ? "No eligible members found. None of the uploaded members were found."
      : notFoundCount === 0
        ? `${foundCount} members will receive this campaign.`
        : `${foundCount} members will receive this campaign. ${notFoundCount} uploaded records could not be matched and will be excluded.`;

  // Columns are derived dynamically from the uploaded file's headers, so the
  // preview keeps working if the template changes (e.g. Customer Contact -> Email).
  const columns = useMemo(() => {
    const keys = rows.length ? Object.keys(rows[0]) : [];
    return [
      {
        title: "No",
        width: 60,
        render: (_: any, __: any, index: number) =>
          (currentPage - 1) * pageSize + index + 1,
      },
      ...keys.map((key) => ({
        title: COLUMN_LABELS[key] ?? key,
        dataIndex: key,
        render: (val: any) => {
          if (key === "Status" && STATUS_DOT_COLOR[val]) {
            return (
              <Flex align="center" gap={6}>
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: STATUS_DOT_COLOR[val],
                    display: "inline-block",
                    flexShrink: 0,
                  }}
                />
                <span>{val}</span>
              </Flex>
            );
          }
          return val === "" || val === null || val === undefined
            ? "-"
            : String(val);
        },
      })),
    ];
  }, [rows, currentPage, pageSize]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      width={isMobile ? 400 : 760}
      style={{ top: 24 }}
      title={
        <Flex align="center" justify="space-between">
          <Title level={5} style={{ margin: 0 }}>
            Preview Target Member List
          </Title>
          <Button onClick={onClose}>Close</Button>
        </Flex>
      }
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={summaryMessage}
      />
      <Table
        className="table-preview"
        size="small"
        scroll={{ x: 500 }}
        rowKey={(_, index) => index!}
        dataSource={rows}
        pagination={{
          current: currentPage,
          pageSize,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50, 100],
          onChange: (page, size) => {
            setCurrentPage(page);
            setPageSize(size);
          },
          showTotal: (total, range) =>
            `Results per Page ${range[1] - range[0] + 1} of ${total} Members`,
        }}
        columns={columns as any}
      />
    </Modal>
  );
};

export default TargetMemberPreviewModal;
